import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useContext } from "react";
import NoDataPlaceholder from "../../../../../components/element/NoDataPlaceholder";
import useAirlineAdminApi from "../../../../../services/api/useAirlineAdminApi";
import { formatToTwoDecimal } from "../../../../../utils/utils";
import { StorageContext } from "../../../../../services/StorageProvider";
import { DATE_FORMAT, AIRPORT_FEES_TYPE, FeeType } from "../../../../../utils/constants";
import {
  getAirportCode,
  getAirportData,
  getFlightNumber,
} from "../../../../../utils/utils";
import "../InvoiceDetail.css";

function FeeInvoiceTable({ feeInvoiceDetails, feeType }) {
  const { airportData } = useContext(StorageContext);
  const { fetchListOfAircraft } = useAirlineAdminApi();

  const LANDING_FEE_VALUE = AIRPORT_FEES_TYPE.find(fee => fee.value === FeeType.LANDING_FEE)?.value;

  const { data: aircraftListData } = useQuery({
    queryKey: ["get-all-aircraft-list"],
    queryFn: fetchListOfAircraft,
    retry: false,
  });

  const getFeeTypeName = (value) => {
    const feeType = AIRPORT_FEES_TYPE.find((fee) => fee.value === value);
    return feeType ? feeType.name : value;
  };

  const getFeeValue = (detail) => {
    const feeBreakdown = detail.feeBreakdownDto;
    if (!feeBreakdown) return 0;

    switch (feeType) {
      case "LANDING_FEE":
        return feeBreakdown.landingFee || 0;
      case "PARKING_FEE":
        return feeBreakdown.parkingFee || 0;
      case "SECURITY_FEE":
        return feeBreakdown.scannedPassengerSecurityFee || 0;
      case "AFTER_HOURS_FEE":
        return (feeBreakdown.arrivalOvertimeFee || 0) + (feeBreakdown.departureOvertimeFee || 0);
      case "ESCORT_FEE":
        return (feeBreakdown.arrivalEscortOfficerFee || 0) +
          (feeBreakdown.arrivalEscortVehicleFee || 0) +
          (feeBreakdown.departureEscortOfficerFee || 0) +
          (feeBreakdown.departureEscortScreeningFee || 0) +
          (feeBreakdown.departureEscortVehicleFee || 0);
      default:
        return 0;
    }
  };

  const getAircraftMTOW = (selectedId) => {
    if (!selectedId || !aircraftListData?.data) return "---";
    const selectedAircraft = aircraftListData?.data?.find(
      (aircraft) => aircraft?.id === selectedId
    );
    return selectedAircraft?.maxTakeOffWeight || "---";
  };

  const getAircraftIcaoCode = (selectedId) => {
    if (!selectedId || !aircraftListData?.data) return "---";
    const selectedAircraft = aircraftListData?.data?.find(
      (aircraft) => aircraft?.id === selectedId
    );
    return selectedAircraft?.icaoCode || "---";
  };

  const renderContent = () => {
    if (feeInvoiceDetails.length === 0) {
      return <NoDataPlaceholder />;
    }

    return (
      <TableContainer component={Paper} className="invoices-data-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Flight Number</TableCell>
              <TableCell>Aircraft Registration</TableCell>
              <TableCell>Aircraft Type</TableCell>
              {feeType === LANDING_FEE_VALUE && <TableCell>MTOW (lbs)</TableCell>}
              <TableCell>Departure Airport</TableCell>
              <TableCell>Arrival Airport</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>{getFeeTypeName(feeType) || "Fee"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feeInvoiceDetails.map((detail, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{getFlightNumber(detail)}</TableCell>
                <TableCell>{detail.aircraftRegistrationNumber || "---"}</TableCell>
                <TableCell>
                  {getAircraftIcaoCode(detail.aircraftTypeId) || "---"}
                </TableCell>
                {feeType === LANDING_FEE_VALUE && (
                  <TableCell>
                    {getAircraftMTOW(detail.aircraftTypeId) || "---"}
                  </TableCell>
                )}
                <TableCell>
                  {getAirportCode(
                    getAirportData(airportData, detail.departureAirportId)
                  )}
                </TableCell>
                <TableCell>
                  {getAirportCode(
                    getAirportData(airportData, detail.arrivalAirportId)
                  )}
                </TableCell>
                <TableCell>
                  {dayjs(detail.flightDate).format(DATE_FORMAT)}
                </TableCell>
                <TableCell>
                  {`BSD ${formatToTwoDecimal(getFeeValue(detail))}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return renderContent();
}

export default FeeInvoiceTable;
